import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { authService } from '../services/auth.service';
import { toast } from 'sonner';
import { setFaroCedenteUser, setFaroAdminUser, clearFaroUser } from '../lib/telemetry';

// Regex to detect CPF-like input (digits only, possibly with dots/dash)
const CPF_PATTERN = /^[\d.-]+$/;
// RFC 4122 UUID pattern
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const useAuthStore = create(
    persist(
        (set, get) => ({
            token: null,
            cpf: null,           // CPF do cedente logado — persiste para re-hidratação após refresh
            sindicatoId: null,
            roleId: null,
            roleName: null,
            permissions: [],     // string[] of allowed resources — source of truth
            isAdminMode: false,  // has 'usuarios' permission → can see admin panel
            isSindicatoMode: false, // has 'sindicato_views' but not 'usuarios'
            isLoggingIn: false,
            isAuthenticated: false,
            skipPortalRedirect: false,

            hasPermission: (resource) => get().permissions.includes(resource),

            login: async (identifier, extra, dataNascimento = null) => {
                set({ isLoggingIn: true });
                try {
                    // UUID login — cedente acessou via link ?id=<uuid> (sem PII na URL)
                    if (UUID_PATTERN.test(identifier.trim())) {
                        const data = await authService.loginById(identifier.trim());
                        set({
                            token: null,
                            cpf: data.cpf ?? null,
                            sindicatoId: null,
                            roleId: null,
                            roleName: null,
                            permissions: [],
                            isAdminMode: false,
                            isSindicatoMode: false,
                            isAuthenticated: true,
                            isLoggingIn: false,
                        });
                        await setFaroCedenteUser(data.cpf ?? identifier);
                        return { success: true, role: 'user', data, cpf: data.cpf };
                    }

                    if (CPF_PATTERN.test(identifier.replace(/\s/g, ''))) {
                        const data = await authService.login(identifier);
                        const requiresDOB = data?.processos_cedente?.some(
                            pc => pc.processo?.requer_data_nascimento === true
                        ) ?? false;
                        if (requiresDOB) {
                            if (!dataNascimento) {
                                set({ isLoggingIn: false });
                                return { success: false, error: "dob_required" };
                            }
                            if (data.data_nascimento && data.data_nascimento !== dataNascimento) {
                                set({ isLoggingIn: false });
                                return { success: false, error: "data_nascimento_incorreta" };
                            }
                        }
                        const activeDeals = (data?.processos_cedente ?? []).filter(
                            pc => pc.processo && !pc.processo.deleted_at
                        );
                        const todosSemValor = activeDeals.length > 0 && activeDeals.every(
                            pc => !pc.valor_proposta_final || Number(pc.valor_proposta_final) === 0
                        );
                        if (todosSemValor) {
                            set({ isLoggingIn: false });
                            return { success: false, error: "valor_zero" };
                        }
                        set({
                            token: null,
                            cpf: identifier,
                            sindicatoId: null,
                            roleId: null,
                            roleName: null,
                            permissions: [],
                            isAdminMode: false,
                            isSindicatoMode: false,
                            isAuthenticated: true,
                            isLoggingIn: false,
                        });
                        await setFaroCedenteUser(identifier);
                        return { success: true, role: 'user', data, cpf: identifier };
                    }

                    const password = extra || '';
                    const jwt = await authService.adminLogin(identifier, password);
                    const permissions = jwt.permissions ?? [];
                    const isAdminMode = permissions.includes('usuarios') || permissions.includes('tema');
                    const isSindicatoMode = permissions.includes('sindicato_views') && !isAdminMode;

                    set({
                        token: jwt.access_token,
                        sindicatoId: jwt.sindicato_id ?? null,
                        roleId: jwt.role_id ?? null,
                        roleName: jwt.role_name ?? null,
                        permissions,
                        isAdminMode,
                        isSindicatoMode,
                        isAuthenticated: true,
                        isLoggingIn: false,
                    });

                    const routeRole = isAdminMode ? 'admin' : isSindicatoMode ? 'sindicato' : 'user';
                    await setFaroAdminUser(jwt.role_name ?? routeRole, identifier);
                    return { success: true, role: routeRole };
                } catch (e) {
                    set({ isLoggingIn: false });
                    toast.error(e.message || "Falha ao conectar à API. Tente novamente mais tarde.");
                    return { success: false, error: e.message };
                }
            },

            logout: () => {
                clearFaroUser();
                try { localStorage.removeItem('theme_cache:' + window.location.hostname); } catch {}
                set({
                    token: null,
                    cpf: null,
                    sindicatoId: null,
                    roleId: null,
                    roleName: null,
                    permissions: [],
                    isAdminMode: false,
                    isSindicatoMode: false,
                    isAuthenticated: false,
                    skipPortalRedirect: true,
                });
            },
        }),
        {
            name: 'assinatura-auth',
            partialize: (state) => ({
                token: state.token,
                cpf: state.cpf,
                sindicatoId: state.sindicatoId,
                roleId: state.roleId,
                roleName: state.roleName,
                permissions: state.permissions,
                isAuthenticated: state.isAuthenticated,
                isAdminMode: state.isAdminMode,
                isSindicatoMode: state.isSindicatoMode,
                skipPortalRedirect: state.skipPortalRedirect,
            }),
        }
    )
);
